import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "@remix-run/react";
import isEmpty from "lodash/isEmpty";
import debounce from "lodash/debounce";

// Utils and Constants
import { feedbackTriggers } from "~/utils/quiz/questionsData";
import { isStylesQuestion } from "~/utils/quiz/questions";
import { useAnswersCounter } from "~/hooks/useAnswersCounter";
import { useRoutePrefix } from "~/hooks/useRoutePrefix";
import { useQuizTracker } from "~/hooks/useQuizTracker";
import { QUIZ_EVENTS, markQuizStartedOnce } from "~/utils/analytics/quizEvents";
import { getAmplitudeIdentity } from "~/utils/analytics/amplitudeIdentity";

// Shared Components
import { ArrowForward } from "~/components/icons/arrow-forward";
import { CrossIcon } from "~/components/icons/cross";
import { SecondaryButton } from "~/components/shared/SecondaryButton";

// Sibling Utils and Components
import { defaultStyle, updateLocation } from "~/utils/quiz/transitions";
import { QuizNavigation } from "../QuizNavigation";
import { SliderQuestion } from "../SliderQuestion";
import { StylesQuestionWrapper } from "../StylesQuestionWrapper";
import { RatingQuestion } from "../RatingQuestion";
import { FeedbackPopup } from "../FeedbackPopup";

import * as styles from "./index.module.scss";
import { useQuizState } from "~/hooks/useQuizState";

export function QuestionLayout({ question, isLastQuestion, step }) {
  const { title, subtitle, type, options, id, useRange = false } = question;

  const navigate = useNavigate();
  const location = useLocation();
  const {
    settings: { answers = {} },
    setAnswer,
  } = useQuizState();
  // TODO: maybe use a better hash function
  const answersHash = useMemo(() => JSON.stringify(answers), [answers]);
  const { countAnswer } = useAnswersCounter();
  const { QUIZ_ROUTE, QUIZ_RESULTS_ROUTE } = useRoutePrefix();
  const track = useQuizTracker();

  /**
   * Context shared by the per-question events, mirroring the legacy payload
   * (question text lowercased for readability/traceability).
   */
  const stepContext = () => ({
    step,
    step_id: id,
    quiz_question_text: title?.toLowerCase(),
  });

  const answerValue = useMemo(() => answers[id], [answersHash, id]);

  const transitionRef = useRef(null);
  const layoutRef = useRef(null);
  const directionRef = useRef(null);
  const lastViewedStepRef = useRef(null);

  /**
   * Derived states
   */
  const isSliderQuestion = type === "slider";
  const isRatingQuestion = type === "rating";
  const isPricingPlanQuestion = type === "pricingPlanList";
  const isColorSelectionQuestion = type === "colorSelection";
  const hasOneRatingAnswer =
    isRatingQuestion && answerValue?.some((ans) => !!ans?.value);

  /**
   * Local States
   */
  const [entered, setEntered] = useState(false);
  const [feedback, setFeedback] = useState({ show: false, text: "" });
  const [hasAnsweredRating, setHasAnsweredRating] =
    useState(hasOneRatingAnswer);

  /**
   * Builds the back and next links based on the current step and whether the question is the last one.
   */
  const backLink = step === 1 ? null : `${QUIZ_ROUTE}/${step - 1}`;
  const nextLink = isLastQuestion
    ? `${QUIZ_ROUTE}/results`
    : `${QUIZ_ROUTE}/${step + 1}`;

  // Entry lifecycle: mount-only, kept separate from analytics so `entered`
  // stays coupled to the component's life, not to step changes.
  useEffect(() => {
    setEntered(true);
    return () => {
      setEntered(false);
    };
  }, []);

  // Analytics: one view per step. Only emit when the `step` prop agrees with the
  // step in the URL — the step loader sets `shouldRevalidate` to false, so
  // during hydration and client-side navigation the prop briefly lags the URL,
  // which would otherwise report a view for a step the user is not on. The URL
  // is the source of truth for what is on screen.
  useEffect(() => {
    const urlStep = Number(location.pathname.split("/").filter(Boolean).pop());
    if (step !== urlStep) return;

    // One view per step. StrictMode double-invokes effects in dev, and any
    // re-render that keeps the same step would otherwise re-fire. The ref
    // persists across the same instance's setup/cleanup/setup, so the duplicate
    // is dropped — while a genuine return to a step (e.g. Back) re-views, since
    // the step changed in between.
    if (lastViewedStepRef.current === step) return;
    lastViewedStepRef.current = step;

    // quiz_started once per session, on the first step viewed with a usable
    // identity — legacy semantics: "entered the quiz", whatever the entry step
    // (a varietal deep link lands on step 2). The pixel writes the identity
    // cookie a few seconds after load, and the server drops identity-less
    // events as unattributable, so the once-per-session flag is only consumed
    // once a device id exists — until then every step view retries.
    try {
      if (getAmplitudeIdentity().deviceId && markQuizStartedOnce()) {
        track(QUIZ_EVENTS.STARTED, { step });
      }
    } catch {
      // The identity read must never break the quiz; the next step retries.
    }
    track(QUIZ_EVENTS.STEP_VIEWED, stepContext());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, location.pathname]);

  /**
   * Constructs the URL for the next page based on the provided parameters.
   * If no parameters are provided, it returns the next page URL without parameters.
   *
   * @param {Object} newQueryParams
   * @returns {String}
   */
  const buildNextDirection = (newQueryParams) => {
    const navigationPath = {
      pathname: nextLink,
    };

    if (!isEmpty(newQueryParams)) {
      const searchParams = new URLSearchParams(newQueryParams);
      const stringifiedSearchParams = searchParams.toString();
      navigationPath.search = `?${stringifiedSearchParams}`;
    }

    return navigationPath;
  };

  /**
   * Triggers Selection Question Event when user clicks the "Results" button on the Pricing Plan Question
   */
  const handlePricingPlanQuestionEvent = () => {
    // evQuizSelectionAnswered({ property: id, value: reduxPricingPlan }, title);
  };

  /**
   * Handles specific scenarios where the question is skipped during the quiz
   */
  const handleSkippedQuestion = async () => {
    // If it's' is a Pricing Plan Question, it should be considered answered, not skipped,
    // even if the user didn't modify the default answer
    if (isPricingPlanQuestion) {
      handlePricingPlanQuestionEvent();
      await handleAnsweredQuestion();
      return;
    }

    // if is rating question and user answered at least on of them, we call it answered not skipped
    if (hasAnsweredRating) {
      await handleAnsweredQuestion();
      return;
    }

    track(QUIZ_EVENTS.QUESTION_SKIPPED, stepContext());
  };

  /**
   * Sets the direction for the next page and triggers relevant events
   * based on whether the question was answered or skipped.
   *
   * @param {Boolean} skipped - Indicates if the question was skipped. Defaults to true.
   * @param {Object} newQueryParams - Parameters to be passed to the next page URL. Defaults to an empty object.
   */
  const next = async (skipped = true, newQueryParams = {}) => {
    directionRef.current = buildNextDirection(newQueryParams);

    // TODO: Fix client side navigation transition
    // layoutRef.current.style["transform"] = "translateX(-100%)";

    if (skipped) {
      handleSkippedQuestion();
    } else {
      await handleAnsweredQuestion();
    }

    if (isLastQuestion) {
      track(QUIZ_EVENTS.FINISHED, { step });
    }

    // TODO: Remove invocation below once we figure out transitions between questions
    onTransitionEnd();
  };

  const handleAnsweredQuestion = async () => {
    countAnswer(id);
    track(QUIZ_EVENTS.QUESTION_ANSWERED, stepContext());
  };

  const back = () => {
    directionRef.current = {
      pathname: backLink,
      search: directionRef.current?.search,
    };

    // TODO: Fix client side navigation transition
    // layoutRef.current.style["transform"] = "translateX(100%)";
    track(QUIZ_EVENTS.QUESTION_BACKED, stepContext());

    // TODO: Remove invocation below once we figure out transitions between questions
    onTransitionEnd();
  };

  const goToResults = () => {
    window.location.href = `${window.location.origin}${QUIZ_RESULTS_ROUTE}`;
  };

  const onTransitionEnd = () => {
    updateLocation();

    if (directionRef.current) {
      if (directionRef.current.pathname.includes("results")) {
        goToResults();
      } else {
        navigate(directionRef.current.pathname, directionRef.current);
        directionRef.current = null;
      }
    }
  };

  const onClose = () => {
    track(QUIZ_EVENTS.CLOSED, stepContext());
    window.location.href = window.location.origin;
  };

  const onClickShowResults = () => {
    track(QUIZ_EVENTS.SKIPPED, stepContext());
    goToResults();
  };

  const onChangeValue = (value) => {
    // Use the centralized state management
    setAnswer(id, value);

    triggerFeedback(value);
    // track rating question answered
    if (isRatingQuestion) {
      setHasAnsweredRating(true);
    }
  };

  const debouncedFeedback = debounce((data, rule) => {
    // Update rule reference in order to track the feedback that has been shown already
    if (!rule.shown) {
      rule.shown = true;
      setFeedback(data);
    }
  }, 450);

  const updatePopup = useCallback(debouncedFeedback, [feedback]);

  const triggerFeedback = (answerValue, cardIdx = 0) => {
    const rules = feedbackTriggers[id];

    if (rules) {
      if (isSliderQuestion) {
        rules.map((rule) => {
          if (
            !rule.value ||
            (rule.value[0] < answerValue && rule.value[1] > answerValue)
          ) {
            updatePopup({ show: true, text: rule.text }, rule);
          }
        });
      } else if (rules[cardIdx]) {
        rules[cardIdx].map((rule) => {
          if (!rule.value || rule.value.includes(answerValue)) {
            updatePopup({ show: true, text: rule.text, rule });
          }
        });
      }
    }
  };

  return (
    // TODO: Fix client side navigation transition to work with Remix useNavigate
    // <Transition in={entered} timeout={0} nodeRef={transitionRef}>
    //   {(status) => (
    <div
      ref={layoutRef}
      className={styles.container}
      // TODO: Fix client side navigation transition to work with Remix useNavigate
      // onTransitionEnd={onTransitionEnd}
      style={{
        ...defaultStyle,
        // TODO: Fix client side navigation transition to work with Remix useNavigate
        // ...getTransitionStyles()[status],
      }}
    >
      {feedback.show && (
        <FeedbackPopup
          text={feedback.text}
          onHidePopup={() => setFeedback({ show: false, text: "" })}
        />
      )}

      <div className={styles.header}>
        <div className={styles.skipToResultsContainer}>
          <SecondaryButton
            className={styles.skipToResultsButton}
            onClick={onClickShowResults}
          >
            Skip to results
            <ArrowForward color="#d5440b" size={14} direction="right" />
          </SecondaryButton>
        </div>
        <div className={styles.closeContainer}>
          <button
            type="button"
            aria-label="close quiz"
            className={styles.close}
            onClick={onClose}
          >
            <CrossIcon />
          </button>
        </div>
      </div>

      <div className={styles.questionWrapper}>
        <div className={styles.content}>
          <h1 className={styles.heading} data-testid="question-title">
            {title}
          </h1>
          {subtitle && <h2 className={styles.subheading}>{subtitle}</h2>}
        </div>

        {isSliderQuestion && (
          <SliderQuestion
            question={question}
            isLastQuestion={isLastQuestion}
            onClickButton={next}
            onChangeValue={onChangeValue}
            answerValue={answerValue}
            useRange={useRange}
          />
        )}

        {isRatingQuestion &&
          (isStylesQuestion(id) ? (
            <StylesQuestionWrapper
              colorAnswer={answers.color}
              answerValue={answerValue}
              quizQuestionText={title}
              onFinish={next}
              onChangeValue={onChangeValue}
            />
          ) : (
            <RatingQuestion
              options={options}
              answerValue={answerValue}
              quizStepId={id}
              quizQuestionText={title}
              onChangeValue={onChangeValue}
              onFinish={next}
            />
          ))}

        <div className={styles.footer}>
          <QuizNavigation
            onBack={back}
            onNext={next}
            isLastQuestion={isLastQuestion}
            disableBack={!backLink}
          />
        </div>
      </div>
    </div>
    //   )}
    // </Transition>
  );
}
